import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Modal,
  TextInput,
  Loader,
  CloseButton,
  Stack,
  Group,
  Text,
  UnstyledButton,
  ScrollArea,
  Box,
} from "@mantine/core";
import { IconGasStation, IconMapPin, IconHistory, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import Fuse, { IFuseOptions } from "fuse.js";

import { usePlaceAutocomplete } from "@/hooks/usePlaceAutocomplete";
import { PlaceData } from "@/hooks/useGoogleMapsAutocomplete";
import { GeoCoords } from "@/hooks/useGeolocation";

import { useLocationHistoryContext } from "@/contexts/LocationHistoryContext";

import { DistanceUnitSystem, formatDistance } from "./placeAutocompleteUtils";

// ============================================
// Constants
// ============================================

const MIN_SEARCH_LENGTH = 2;
const MAX_RECENT_ON_FOCUS = 5;
const MAX_RECENT_WHILE_TYPING = 3;
const FUSE_THRESHOLD = 0.4;

/** Delay for auto-focus after fullscreen modal animation completes */
const AUTOFOCUS_DELAY_MS = 300;

// ============================================
// Fuse.js configuration
// ============================================

const FUSE_OPTIONS: IFuseOptions<PlaceData> = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "address.address1", weight: 0.15 },
    { name: "address.displayName", weight: 0.15 },
  ],
  threshold: FUSE_THRESHOLD,
  includeScore: false,
};

// ============================================
// Types
// ============================================

export interface PlacePickerModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Callback to close the modal */
  onClose: () => void;

  /** Callback when a place is selected */
  onSelect: (name: string, placeData: PlaceData | null) => void;

  /** Current input value to pre-populate search */
  initialValue?: string;

  /** Recent / saved places */
  recentPlaces?: PlaceData[];

  /**
   * Location bias for relevant nearby results.
   * If not provided, the modal will silently request the user's
   * geolocation on open and use it as the bias.
   */
  locationBias?: GeoCoords | null;

  /** Place types to search for */
  placeTypes?: string[];

  /** Search radius in meters */
  radiusMeters?: number;

  /** Placeholder text */
  placeholder?: string;

  /** Modal title */
  title?: string;

  /** Whether to show distance */
  showDistance?: boolean;

  /** Unit system for distance */
  distanceUnit?: DistanceUnitSystem;
}

// ============================================
// Component
// ============================================

export const PlacePickerModal: React.FC<PlacePickerModalProps> = ({
  opened,
  onClose,
  onSelect,
  initialValue = "",
  recentPlaces = [],
  locationBias = null,
  placeTypes = ["gas_station"],
  radiusMeters,
  placeholder = "Search for a gas station",
  title = "Select Place",
  showDistance = true,
  distanceUnit = "km",
}) => {
  const { t } = useTranslation("common");

  const {
    placePredictions,
    loading,
    fetchPlacePredictions,
    fetchPlaceDetails,
    clearPredictions,
  } = usePlaceAutocomplete({ includedTypes: placeTypes, radiusMeters });

  const {
    requestLocation,
  } = useLocationHistoryContext();

  const [searchValue, setSearchValue] = useState("");
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const locationBiasRef = useRef(locationBias);
  const searchValueRef = useRef("");

  useEffect(() => {
    locationBiasRef.current = locationBias;
  }, [locationBias]);

  // Keep searchValueRef in sync so the geolocation callback reads current value
  useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  // ============================================
  // Fuse.js index for recent places
  // ============================================

  const fuseIndex = useMemo(() => {
    if (recentPlaces.length === 0) return null;
    return new Fuse(recentPlaces, FUSE_OPTIONS);
  }, [recentPlaces]);

  // ============================================
  // Filtered recent places
  // ============================================

  const filteredRecentPlaces = useMemo(() => {
    if (recentPlaces.length === 0) return [];

    const query = searchValue?.trim() || "";

    if (query.length === 0) {
      return recentPlaces.slice(0, MAX_RECENT_ON_FOCUS);
    }

    if (!fuseIndex) return [];

    const results = fuseIndex.search(query);
    return results
      .slice(0, MAX_RECENT_WHILE_TYPING)
      .map((result) => result.item);
  }, [recentPlaces, fuseIndex, searchValue]);

  // Initialize when modal opens
  useEffect(() => {
    if (opened) {
      setSearchValue(initialValue);
      if (initialValue && initialValue.trim().length >= MIN_SEARCH_LENGTH) {
        fetchPlacePredictions(initialValue, locationBiasRef.current);
      }

      // Silently request geolocation so place results are biased to the user's area.
      // If permission was already granted this resolves instantly;
      // if not, the browser will show its native prompt.
      // Once we have coords, re-fetch predictions so the user sees local results.
      requestLocation({
        reverseGeocode: false,
        allowPrompt: true,
      })
        .then((result) => {
          if (result?.coords) {
            locationBiasRef.current = result.coords;

            // Re-fetch with location bias if the user has already typed something
            const currentSearch = searchValueRef.current;
            if (currentSearch && currentSearch.trim().length >= MIN_SEARCH_LENGTH) {
              fetchPlacePredictions(currentSearch, result.coords);
            }
          }
        })
        .catch((err) => {
          console.error("Failed to acquire location for place search:", err);
        });

      // Focus input after fullscreen modal animation completes
      setTimeout(() => {
        inputRef.current?.focus();
      }, AUTOFOCUS_DELAY_MS);
    } else {
      setSearchValue("");
      clearPredictions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((newValue: string) => {
    setSearchValue(newValue);

    if (newValue && newValue.trim().length >= MIN_SEARCH_LENGTH) {
      fetchPlacePredictions(newValue, locationBiasRef.current);
    } else {
      clearPredictions();
    }
  }, [fetchPlacePredictions, clearPredictions]);

  /**
   * Handle clear search
   */
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    clearPredictions();
    inputRef.current?.focus();
  }, [clearPredictions]);

  /**
   * Handle recent place selection
   */
  const handleRecentPlaceSelect = useCallback((place: PlaceData) => {
    onSelect(place.name || "", place);
    onClose();
  }, [onSelect, onClose]);

  /**
   * Handle Google prediction selection
   */
  const handleGooglePlaceSelect = useCallback(async (
    placeId: string,
    placeName: string
  ) => {
    setIsLoadingDetails(true);

    try {
      const placeData = await fetchPlaceDetails(placeId);

      if (placeData) {
        onSelect(placeData.name || placeName, placeData);
      } else {
        onSelect(placeName, null);
      }
    } catch (err) {
      console.error("Failed to fetch place details:", err);
      onSelect(placeName, null);
    } finally {
      setIsLoadingDetails(false);
      onClose();
    }
  }, [fetchPlaceDetails, onSelect, onClose]);

  /**
   * Handle using typed text as place name
   */
  const handleUseTypedName = useCallback(() => {
    onSelect(searchValue.trim(), null);
    onClose();
  }, [searchValue, onSelect, onClose]);

  const hasRecentPlaces = filteredRecentPlaces.length > 0;
  const hasGooglePredictions = placePredictions.length > 0;
  const hasSuggestions = hasRecentPlaces || hasGooglePredictions;
  const isLoading = loading || isLoadingDetails;

  const placeIcon =
    placeTypes.includes("gas_station") ? (
      <IconGasStation size={22} style={{ opacity: 0.6 }} />
    ) : (
      <IconMapPin size={22} style={{ opacity: 0.6 }} />
    );

  /**
   * Render distance badge
   */
  const renderDistance = useCallback(
    (distanceMeters: number | null | undefined) => {
      if (!showDistance || distanceMeters == null || distanceMeters < 0) {
        return null;
      }
      return (
        <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          {formatDistance(distanceMeters, distanceUnit)}
        </Text>
      );
    },
    [showDistance, distanceUnit]
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      fullScreen
      withCloseButton
      styles={{
        body: { padding: 0, display: "flex", flexDirection: "column", height: "calc(100vh - 60px)" },
        header: { padding: "12px 16px" },
      }}
    >
      {/* Search Input */}
      <Box px="md" pt="xs" pb="sm">
        <TextInput
          ref={inputRef}
          placeholder={placeholder}
          value={searchValue}
          onChange={(event) => handleSearchChange(event.currentTarget.value)}
          leftSection={<IconSearch size={20} />}
          rightSection={
            isLoading ? (
              <Loader size={20} />
            ) : searchValue ? (
              <CloseButton
                size="md"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClearSearch}
                aria-label={t("placeAutocomplete.clearSearch", "Clear search")}
              />
            ) : null
          }
          size="lg"
        />
      </Box>

      {/* Results */}
      <ScrollArea flex={1} px="md">
        <Stack gap={0}>
          {/* No results */}
          {!hasSuggestions && searchValue.length >= MIN_SEARCH_LENGTH && !loading && (
            <Text c="dimmed" ta="center" py="xl" size="md">
              {t("placeAutocomplete.noPlacesFound", "No places found")}
            </Text>
          )}

          {/* Type more hint */}
          {!hasSuggestions && searchValue.length > 0 && searchValue.length < MIN_SEARCH_LENGTH && !loading && (
            <Text c="dimmed" ta="center" py="xl" size="md">
              {t("placeAutocomplete.typeAtLeastNChars", "Type at least {{count}} characters to search", { count: MIN_SEARCH_LENGTH })}
            </Text>
          )}

          {/* Recent Places */}
          {hasRecentPlaces && (
            <>
              <Text size="sm" fw={600} c="dimmed" tt="uppercase" py="xs">
                {t("placeAutocomplete.recentPlaces", "Recent Places")}
              </Text>
              {filteredRecentPlaces.map((place, index) => (
                <UnstyledButton
                  key={`recent-${place.name}-${index}`}
                  onClick={() => handleRecentPlaceSelect(place)}
                  py="md"
                  px="sm"
                  w="100%"
                  style={{ borderRadius: "var(--mantine-radius-sm)", minHeight: 48 }}
                >
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <div style={{ paddingTop: 2, flexShrink: 0 }}>
                      <IconHistory size={22} style={{ opacity: 0.5 }} />
                    </div>
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Text size="md" fw={500} truncate>
                        {place.name}
                      </Text>
                      {place.address?.address1 && (
                        <Text size="sm" c="dimmed" truncate>
                          {place.address.address1}
                          {place.address.city && `, ${place.address.city}`}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                </UnstyledButton>
              ))}
            </>
          )}

          {/* Google Predictions */}
          {hasGooglePredictions && (
            <>
              {hasRecentPlaces && (
                <Text size="sm" fw={600} c="dimmed" tt="uppercase" py="xs" mt="xs">
                  {t("placeAutocomplete.searchResults", "Search Results")}
                </Text>
              )}
              {placePredictions.map((prediction) => (
                <UnstyledButton
                  key={prediction.placeId}
                  onClick={() =>
                    handleGooglePlaceSelect(prediction.placeId, prediction.name)
                  }
                  py="md"
                  px="sm"
                  w="100%"
                  style={{ borderRadius: "var(--mantine-radius-sm)", minHeight: 48 }}
                >
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <div style={{ paddingTop: 2, flexShrink: 0 }}>{placeIcon}</div>
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap" justify="space-between">
                        <Text size="md" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
                          {prediction.name}
                        </Text>
                        {renderDistance(prediction.distanceMeters)}
                      </Group>
                      {prediction.address && (
                        <Text size="sm" c="dimmed" truncate>
                          {prediction.address}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                </UnstyledButton>
              ))}
            </>
          )}

          {/* Use typed text as-is */}
          {searchValue.trim().length >= MIN_SEARCH_LENGTH && !isLoadingDetails && (
            <UnstyledButton
              onClick={handleUseTypedName}
              py="md"
              px="sm"
              mt="xs"
              w="100%"
              style={{ borderRadius: "var(--mantine-radius-sm)", minHeight: 48 }}
            >
              <Group gap="sm" wrap="nowrap">
                <IconSearch size={22} style={{ flexShrink: 0 }} />
                <Text size="md" c="dimmed">
                  {t("placeAutocomplete.useTypedName", 'Use "{{name}}"', { name: searchValue.trim() })}
                </Text>
              </Group>
            </UnstyledButton>
          )}
        </Stack>
      </ScrollArea>
    </Modal>
  );
};